import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import LocalizedLink from '@/components/shared/LocalizedLink';
import { useLanguage } from '@/i18n/LanguageContext';
import {
  marketAnalysesApi,
  personasApi,
  positioningsApi,
  businessModelsApi,
  financialModelsApi,
  marketingStrategiesApi,
  mvpPlansApi,
} from '@/utils/api-pb';
import type { ContextOverrides, ContextSource } from '@/types/methodologies';

/** "Every persona" — the backend's default for personas is up to three latest, not one. */
const ALL_PERSONAS_VALUE = '__all__';
const OFF_VALUE = '__off__';

interface ContextOption {
  id: string;
  name?: string;
  created: string;
}

interface SourceMeta {
  /** Page where the user creates this artifact when the project has none. */
  createPath: string;
  load: (projectId: string) => Promise<ContextOption[]>;
}

type AnyRecord = {
  id: string;
  created: string;
  status?: string;
  generated_text?: string;
  ai_recommendations?: string;
  product_description?: string;
  persona_name?: string;
  model_name?: string;
};

// Mirrors backend/src/services/project-context.ts: lifecycle collections are
// read only when completed, and a record without text adds nothing to a prompt.
function usable(records: AnyRecord[], opts: { requireCompleted?: boolean; textField?: keyof AnyRecord } = {}): AnyRecord[] {
  const textField = opts.textField ?? 'generated_text';
  // The API lists don't share a sort order; newest first makes index 0 the backend's default.
  return records
    .filter((r) => (!opts.requireCompleted || r.status === 'completed') && Boolean(r[textField]))
    .sort((a, b) => b.created.localeCompare(a.created));
}

const SOURCES: Record<ContextSource, SourceMeta> = {
  market_analyses: {
    createPath: '/market-analysis',
    load: async (projectId) => usable(await marketAnalysesApi.list(projectId), { requireCompleted: true })
      .map((r) => ({ id: r.id, name: r.product_description, created: r.created })),
  },
  personas: {
    createPath: '/personas',
    load: async (projectId) => usable(await personasApi.list(projectId))
      .map((r) => ({ id: r.id, name: r.persona_name, created: r.created })),
  },
  positionings: {
    createPath: '/positioning',
    load: async (projectId) => usable(await positioningsApi.list(projectId))
      .map((r) => ({ id: r.id, created: r.created })),
  },
  business_models: {
    createPath: '/business-model',
    load: async (projectId) => usable(await businessModelsApi.list(projectId))
      .map((r) => ({ id: r.id, created: r.created })),
  },
  financial_models: {
    createPath: '/financial-model',
    load: async (projectId) => usable(await financialModelsApi.list(projectId), { requireCompleted: true, textField: 'ai_recommendations' })
      .map((r) => ({ id: r.id, name: r.model_name, created: r.created })),
  },
  marketing_strategies: {
    createPath: '/marketing-strategy',
    load: async (projectId) => usable(await marketingStrategiesApi.list(projectId), { requireCompleted: true })
      .map((r) => ({ id: r.id, created: r.created })),
  },
  mvp_plans: {
    createPath: '/mvp-plan',
    load: async (projectId) => usable(await mvpPlansApi.list(projectId))
      .map((r) => ({ id: r.id, created: r.created })),
  },
};

interface MethodologyContextPickerProps {
  projectId: string;
  /** Sources to offer. Personas are left out by the caller when the form has its own persona field. */
  sources: ContextSource[];
  onChange: (overrides: ContextOverrides) => void;
  disabled: boolean;
}

/**
 * Shows which project materials the generation will read and lets the user
 * swap one for an older version or leave it out. By default each source uses
 * its latest record — exactly what the backend picks on its own, so only
 * deviations from that default are sent as overrides.
 */
export function MethodologyContextPicker({ projectId, sources, onChange, disabled }: MethodologyContextPickerProps) {
  const { t } = useTranslation('methodologies');
  const { lang } = useLanguage();

  const [options, setOptions] = useState<Partial<Record<ContextSource, ContextOption[]>>>({});
  const [picks, setPicks] = useState<Partial<Record<ContextSource, string>>>({});
  const [isLoading, setIsLoading] = useState(true);

  const sourcesKey = sources.join(',');

  useEffect(() => {
    // A slow response for the previous project must not land on the current one.
    let ignore = false;
    setIsLoading(true);
    setOptions({});
    setPicks({});
    onChange({});

    Promise.all(
      sources.map(async (source) => {
        try {
          return [source, await SOURCES[source].load(projectId)] as const;
        } catch (error) {
          console.error(`Error loading ${source} for methodology context:`, error);
          return [source, []] as const;
        }
      }),
    ).then((entries) => {
      if (ignore) return;
      setOptions(Object.fromEntries(entries));
      setIsLoading(false);
    });

    return () => { ignore = true; };
    // onChange is a state setter wrapper from the parent; the reload is keyed on project and sources.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, sourcesKey]);

  const defaultValue = (source: ContextSource): string => {
    if (source === 'personas') return ALL_PERSONAS_VALUE;
    return options[source]?.[0]?.id ?? OFF_VALUE;
  };

  const handlePick = (source: ContextSource, value: string) => {
    const next = { ...picks, [source]: value };
    setPicks(next);

    const overrides: ContextOverrides = {};
    for (const [key, picked] of Object.entries(next) as Array<[ContextSource, string]>) {
      if (picked === defaultValue(key)) continue;
      overrides[key] = picked === OFF_VALUE ? null : picked;
    }
    onChange(overrides);
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(lang === 'en' ? 'en-GB' : 'ru-RU');

  const optionLabel = (option: ContextOption, isLatest: boolean) => {
    const name = option.name?.trim();
    // Date first: in the one-line trigger a long name gets cut, the date must not.
    const base = name
      ? `${formatDate(option.created)} · ${name.length > 60 ? `${name.slice(0, 60)}…` : name}`
      : t('context.fromDate', { date: formatDate(option.created) });
    return isLatest ? t('context.latest', { label: base }) : base;
  };

  return (
    <div className="flex flex-col gap-[12px]">
      <div className="flex flex-col gap-[4px]">
        <Label className="font-segoe text-[14px] text-[#565858]">{t('context.title')}</Label>
        <p className="font-segoe text-[12px] leading-[16px] text-[#969696]">{t('context.hint')}</p>
      </div>

      {sources.map((source) => {
        const list = options[source] ?? [];
        return (
          <div key={source} className="flex flex-col gap-[6px]">
            <span className="font-segoe text-[13px] leading-[18px] text-[#0d0d0d]">
              {t(`context.sources.${source}`)}
            </span>
            {isLoading ? (
              <span className="font-segoe text-[13px] leading-[18px] text-[#969696]">{t('context.loading')}</span>
            ) : list.length === 0 ? (
              <span className="font-segoe text-[13px] leading-[18px] text-[#969696]">
                {t('context.missing')}{' '}
                <LocalizedLink to={SOURCES[source].createPath} className="text-[#2197f1] hover:underline">
                  {t('context.create')}
                </LocalizedLink>
              </span>
            ) : (
              <Select
                value={picks[source] ?? defaultValue(source)}
                onValueChange={(value) => handlePick(source, value)}
                disabled={disabled}
              >
                <SelectTrigger aria-label={t(`context.sources.${source}`)}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {source === 'personas' && (
                    <SelectItem value={ALL_PERSONAS_VALUE}>{t('context.allPersonas')}</SelectItem>
                  )}
                  {list.map((option, index) => (
                    <SelectItem key={option.id} value={option.id}>
                      {optionLabel(option, source !== 'personas' && index === 0)}
                    </SelectItem>
                  ))}
                  <SelectItem value={OFF_VALUE}>{t('context.off')}</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        );
      })}
    </div>
  );
}
